/**
 * Entra ID authentication.
 *
 * Entra answers who you are. It does not answer what you may do — that comes from
 * the API's roleAssignments, exposed through /api/v1/me. The frontend never decides
 * permissions; it renders the projection the server returns.
 *
 * Auth-code flow with PKCE via MSAL. No implicit grant, no tokens in localStorage
 * beyond MSAL's own cache, and no client secret — a SPA cannot keep one.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser'

export interface AuthConfig {
  tenantId: string
  clientId: string
}

/**
 * Runtime configuration, injected by the deployment rather than baked into the
 * bundle.
 *
 * The same built artefact has to work in dev and on the cluster, and rebuilding an
 * image to change a tenant id is the sort of thing that produces a wrong tenant id.
 */
declare global {
  interface Window {
    __QMS_CONFIG__?: Partial<AuthConfig> & { apiBase?: string }
  }
}

const cfg: AuthConfig = {
  tenantId: window.__QMS_CONFIG__?.tenantId ?? import.meta.env.VITE_ENTRA_TENANT_ID ?? '',
  clientId: window.__QMS_CONFIG__?.clientId ?? import.meta.env.VITE_ENTRA_CLIENT_ID ?? '',
}

export const authConfigured = Boolean(cfg.tenantId && cfg.clientId)

/**
 * Development bypass, mirroring the API's AUTH_DISABLED.
 *
 * Two conditions, both required. `import.meta.env.DEV` is statically false in a
 * production build, so the bundler drops this branch entirely — no environment
 * variable can switch it on in a deployed artefact. The explicit opt-in on top means
 * it is never inferred from configuration: deriving it from "no tenant configured"
 * meant the committed production config.js silently disabled local development, and
 * editing that file back and forth is how the wrong one gets shipped.
 *
 * Set VITE_DEV_BYPASS=true in .env.development.local (gitignored) to use it.
 */
export const devBypass =
  import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS === 'true'

/** True when a bearer token should be attached — i.e. not in the dev bypass. */
export const useTokens = authConfigured && !devBypass

/**
 * The scope on the QMS's own API.
 *
 * Requesting only Graph scopes would yield a Graph token, which the API correctly
 * rejects because its audience is graph.microsoft.com rather than the QMS.
 */
const apiScope = cfg.clientId ? `api://${cfg.clientId}/access_as_user` : ''

const msalConfig: Configuration = {
  auth: {
    clientId: cfg.clientId,
    authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Session storage rather than local: a shared or kiosk browser should not keep
    // a QMS session after the tab closes, and this app holds the map of everything
    // valuable at ABAX.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

let msal: PublicClientApplication | null = null

export async function initAuth(): Promise<void> {
  if (!authConfigured) return
  msal = new PublicClientApplication(msalConfig)
  await msal.initialize()
  // Completes the redirect leg when returning from Entra.
  const result = await msal.handleRedirectPromise()
  if (result?.account) msal.setActiveAccount(result.account)
  else {
    const [first] = msal.getAllAccounts()
    if (first) msal.setActiveAccount(first)
  }
}

export function account(): AccountInfo | null {
  return msal?.getActiveAccount() ?? null
}

export function signedIn(): boolean {
  return Boolean(account())
}

export async function signIn(): Promise<void> {
  if (!msal) throw new Error('authentication is not configured')
  await msal.loginRedirect({ scopes: [apiScope, 'openid', 'profile'] })
}

export async function signOut(): Promise<void> {
  if (!msal) return
  await msal.logoutRedirect({ account: account() ?? undefined })
}

/**
 * Returns a bearer token for the QMS API, refreshing silently when possible.
 *
 * A silent failure escalates to a redirect rather than throwing: a token expiring
 * mid-session should look like a brief redirect, not a broken page.
 */
export async function accessToken(): Promise<string | null> {
  if (!msal) return null
  const acct = account()
  if (!acct) return null

  try {
    const res = await msal.acquireTokenSilent({ scopes: [apiScope], account: acct })
    return res.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [apiScope] })
      return null
    }
    throw err
  }
}
